import { ChevronDown, ChevronRight } from 'lucide-react'

/** Sortable column headers shared by the Traffic trees. */
export function TrafficTreeSortHeaders({ nameLabel, sort, onSort }) {
  const columns = [['name', nameLabel], ['pageviews', 'Views'], ['visitors', 'Visitors'], ['emails', 'Emails'], ['payments', 'Payments'], ['cvr', 'CVR']]
  return columns.map(([column, label]) => (
    <button
      key={column}
      type="button"
      className={`${column === 'name' ? 'text-left' : 'text-right'} hover:text-primary whitespace-nowrap`}
      onClick={() => onSort(current => ({
        column,
        direction: current.column === column ? (current.direction === 'desc' ? 'asc' : 'desc') : column === 'name' ? 'asc' : 'desc',
      }))}
      title={`Sort ${label} ${column === 'name' ? 'alphabetically' : 'high or low'}`}
      aria-label={`${label}${sort.column === column ? `, sorted ${sort.direction === 'asc' ? 'ascending' : 'descending'}` : ''}. Click to sort.`}
    >
      {label}<span className="ml-0.5 text-[10px]" aria-hidden="true">{sort.column === column ? (sort.direction === 'desc' ? '▼' : '▲') : '↕'}</span>
    </button>
  ))
}

/** One row of Traffic's source → campaign → content tree, and its open children. */
export default function SourceTreeNode({ node, depth, expandedSources, toggleSource, formatNumber, getSourceColor }) {
  const isExpanded = expandedSources.has(node.id)
  const hasChildren = node.children && node.children.length > 0
  
  // Get icon based on node type
  const getIcon = () => {
    if (node.isSource) {
      // Source category icons
      const icons = {
        meta: '📘',
        google: '🔍',
        everflow: '🔗',
        katalys: '🤝',
        reddit: '👽',
        email: '📧',
        sms: '💬',
        tiktok: '🎵',
        bing: '🔎',
        organic_search: '🌿',
        direct: '🎯',
        other: '📊',
      }
      return icons[node.sourceId] || '📊'
    }
    if (node.isCampaign) return '📁' // Campaign folder
    if (node.isContent) return '📄' // Content/ad
    return hasChildren ? '📂' : '📋'
  }
  
  const cvr = node.visitors > 0 ? (node.payments / node.visitors * 100) : 0
  const childCount = hasChildren ? ` (${node.children.length})` : ''
  
  return (
    <>
      <div 
        className={`grid grid-cols-[1fr_70px_70px_70px_70px_60px] gap-2 py-1.5 text-sm hover:bg-muted/30 rounded cursor-pointer transition-colors ${
          depth === 0 ? 'border-b border-muted/30' : ''
        }`}
        onClick={() => hasChildren && toggleSource(node.id)}
      >
        <div 
          className="flex items-center gap-1 min-w-0"
          style={{ paddingLeft: `${depth * 16}px` }}
        >
          {hasChildren ? (
            <button 
              className="p-0.5 hover:bg-muted rounded shrink-0"
              onClick={(e) => {
                e.stopPropagation()
                toggleSource(node.id)
              }}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <span className="w-5 shrink-0" />
          )}
          
          <span className="shrink-0">{getIcon()}</span>
          
          {node.isSource && getSourceColor && (
            <span 
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: getSourceColor(node.sourceId) }}
            />
          )}
          
          <span 
            className={`text-xs truncate ${node.isSource ? 'font-semibold' : node.isCampaign ? 'font-medium' : ''}`}
            title={node.fullName || node.name}
          >
            {node.name}{node.isSource && childCount}
          </span>
        </div>
        <div className="text-right text-muted-foreground text-xs">{formatNumber(node.pageviews || 0)}</div>
        <div className="text-right text-xs">{formatNumber(node.visitors)}</div>
        <div className="text-right text-muted-foreground text-xs">{formatNumber(node.emails)}</div>
        <div className="text-right text-xs">{formatNumber(node.payments)}</div>
        <div 
          className="text-right font-medium text-xs"
          style={{ 
            color: cvr >= 10 ? '#10B981' : cvr >= 5 ? '#F59E0B' : '#6B7280'
          }}
        >
          {cvr.toFixed(1)}%
        </div>
      </div>
      
      {/* Render children if expanded */}
      {isExpanded && hasChildren && (
        <div 
          className="border-l-2 ml-3" 
          style={{ borderColor: getSourceColor?.(node.sourceId) || '#e5e7eb' }}
        >
          {node.children.map((child) => (
            <SourceTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedSources={expandedSources}
              toggleSource={toggleSource}
              formatNumber={formatNumber}
              getSourceColor={getSourceColor}
            />
          ))}
        </div>
      )}
    </>
  )
}
